import { queryOptions } from '@tanstack/react-query'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import type { ReportQueryKeyEntries } from '@/lib/services/reports/reports.api'
import { getBookedSales } from './money.api'
import type { BookedSalesFilters } from './money.schema'
import { isRecord } from '@/lib/utils'

interface BookedSalesScope {
  organizationId?: OrganizationIdInput
  filters: BookedSalesFilters
}

/** What a sales report caller shows: a query result or its parts. */
interface SalesReportState {
  data: unknown
  isError: boolean
  error: unknown
}

// A failed request's axios response, if the server answered.
function errorResponse(error: unknown) {
  const response = isRecord(error) ? error.response : undefined

  return isRecord(response) ? response : undefined
}

export function isIncompleteSalesReport(error: unknown): boolean {
  const data = errorResponse(error)?.data

  return isRecord(data) && data.code === 'sales_report_incomplete'
}

// A 409 means the report's financial evidence is incomplete, so retrying can't help.
export function retrySalesReport(failureCount: number, error: unknown): boolean {
  return errorResponse(error)?.status !== 409 && failureCount < 2
}

// A failed refresh can retain a previously complete report, but not when the
// server rejects its financial evidence or the user's access to it.
export function canDisplaySalesReport({ data, isError, error }: SalesReportState): boolean {
  if (!data) return false
  if (!isError) return true
  const status = errorResponse(error)?.status
  return typeof status !== 'number' || !status || status >= 500 || status === 408 || status === 429
}

function bookedSalesQueryKey({
  organizationId,
  filters,
}: BookedSalesScope): [AdminQueryRoot & ReportQueryKeyEntries<BookedSalesFilters>] {
  return buildScopedAdminQueryKey(buildAdminQueryRoot({ entity: 'report' }, { organizationId }), {
    organizationId,
    scope: 'bookedSales',
    filters,
  })
}

export const moneyQueries = {
  sales: ({ organizationId, filters }: BookedSalesScope) =>
    queryOptions({
      queryKey: bookedSalesQueryKey({ organizationId, filters }),
      queryFn: getBookedSales,
      retry: retrySalesReport,
    }),
}
